/*
 * OpenSeadragon - TileLoadScheduler
 *
 * Copyright (C) 2009 CodePlex Foundation
 * Copyright (C) 2010-2025 OpenSeadragon contributors
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are permitted provided that the following conditions are
 * met:
 *
 * - Redistributions of source code must retain the above copyright notice,
 *   this list of conditions and the following disclaimer.
 *
 * - Redistributions in binary form must reproduce the above copyright
 *   notice, this list of conditions and the following disclaimer in the
 *   documentation and/or other materials provided with the distribution.
 *
 * - Neither the name of CodePlex Foundation nor the names of its
 *   contributors may be used to endorse or promote products derived from
 *   this software without specific prior written permission.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR
 * A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT
 * OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL,
 * SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT
 * LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE,
 * DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY
 * THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT
 * (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE
 * OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 */

(function( $ ){

// Requests are bounded per origin, because that is what the browser and the server both bound: an HTTP/1.1
// origin gets about six connections, an HTTP/2 origin multiplexes over one and tolerates far more.
const BASE_LIMITS = {
    'http/1.1': 6,
    'http/1.0': 6,
    h2: 24,
    h2c: 24,
    h3: 24
};
// Conservative: over-requesting on HTTP/1.1 costs real head-of-line blocking, while under-requesting on
// HTTP/2 only costs a little latency.
const UNKNOWN_LIMIT = 6;
const MIN_LIMIT = 2;
// Tiles that never yield an origin (data: URLs, blobs, custom loaders) all share one budget.
const FALLBACK_ORIGIN = '*';
// A navigator draws a thumbnail of an image the main viewer is already fetching; it should never compete
// with it for the origin's budget on equal terms.
const NAVIGATOR_LIMIT = 1;

const PROTOCOL_ALIASES = {
    'http/2': 'h2',
    http2: 'h2',
    'http/3': 'h3',
    http3: 'h3',
    'http1.1': 'http/1.1',
    'http/1': 'http/1.1'
};

/**
 * @class TileLoadScheduler
 * @memberof OpenSeadragon
 * @classdesc Decides how many tile downloads may be in flight, and which tiles they should be.
 *
 * There is one instance for the whole page ({@link OpenSeadragon.tileLoadScheduler}), because the limit that
 * matters is per origin and several viewers on a page routinely share one tile server. Dispatch is driven by
 * request completion rather than by the frame loop, so download throughput follows network latency instead
 * of the client's frame rate.
 *
 * Each participating {@link OpenSeadragon.TiledImage} publishes a ranked wish list once per frame; the
 * scheduler round-robins over the images and takes the head of each list while the origin has capacity.
 * Because a wish list is rebuilt from scratch every frame, a tile that is no longer wanted simply stops
 * being offered - no queue has to be cleared to abandon it.
 */
$.TileLoadScheduler = function() {
    // origin -> { inFlight: Number, protocol: String|null }
    this._origins = new Map();
    this._images = [];
    this._cursor = 0;
    this._observer = null;
    this._observing = false;
};

/** @lends OpenSeadragon.TileLoadScheduler.prototype */
$.TileLoadScheduler.prototype = {

    /**
     * Normalize a protocol identifier. Accepts the ALPN ids reported by the Performance API as well as the
     * friendlier spellings a developer is likely to write.
     * @param {String|Boolean} protocol
     * @returns {String|null} a key of BASE_LIMITS, or null if unrecognized
     */
    normalizeProtocol: function(protocol) {
        if (!protocol || protocol === true || protocol === 'auto') {
            return null;
        }
        const key = String(protocol).toLowerCase();
        const aliased = PROTOCOL_ALIASES[key] || key;
        return BASE_LIMITS[aliased] ? aliased : null;
    },

    /**
     * The origin a URL belongs to, for budgeting purposes.
     * @param {String} url
     * @returns {String}
     */
    originOf: function(url) {
        if (!url || typeof url !== 'string') {
            return FALLBACK_ORIGIN;
        }
        try {
            // Falls through to the shared bucket below wherever URL or its origin is unavailable.
            // eslint-disable-next-line compat/compat
            const origin = new URL(url, window.location.href).origin;
            // Opaque origins (data:, blob: in some browsers) stringify to "null"; they are not a real
            // connection pool, so they go in the shared bucket.
            return (!origin || origin === 'null') ? FALLBACK_ORIGIN : origin;
        } catch (e) {
            return FALLBACK_ORIGIN;
        }
    },

    /**
     * Register a tiled image that wants scheduled loading.
     * @param {OpenSeadragon.TiledImage} tiledImage
     */
    register: function(tiledImage) {
        if (this._images.indexOf(tiledImage) === -1) {
            this._images.push(tiledImage);
        }
        if (tiledImage.serverProtocol === 'auto') {
            this._startObserving();
        }
    },

    /**
     * Stop scheduling for a tiled image. Must be called when the image is destroyed, or the scheduler keeps
     * it - and everything it references - alive for the lifetime of the page.
     * @param {OpenSeadragon.TiledImage} tiledImage
     */
    unregister: function(tiledImage) {
        const index = this._images.indexOf(tiledImage);
        if (index > -1) {
            this._images.splice(index, 1);
            if (this._cursor > index) {
                this._cursor--;
            }
        }
    },

    /**
     * @returns {Boolean} whether any image is currently using the scheduler
     */
    isEnabled: function() {
        return this._images.length > 0;
    },

    /**
     * Book a slot on an origin. Called when a request is dispatched, not when it reaches the wire: a job
     * waiting behind imageLoaderLimit, or staged for batching, is still work we have committed to.
     * @param {String} [origin] falsey means the shared budget
     */
    noteStart: function(origin) {
        const record = this._recordFor(origin);
        record.inFlight++;
    },

    /**
     * Release a slot on an origin.
     * @param {String} [origin] falsey means the shared budget
     */
    noteFinish: function(origin) {
        const record = this._recordFor(origin);
        record.inFlight = Math.max(0, record.inFlight - 1);
    },

    /**
     * Record the wire protocol observed for an origin.
     * @param {String} origin
     * @param {String} protocol
     */
    noteProtocol: function(origin, protocol) {
        const normalized = this.normalizeProtocol(protocol);
        if (normalized) {
            this._recordFor(origin).protocol = normalized;
        }
    },

    /**
     * How many concurrent requests this origin should carry right now. A pure function of current state -
     * callers that need it more than once per frame cache the result themselves.
     * @param {String} [origin] falsey means the shared budget
     * @returns {Number}
     */
    limitFor: function(origin) {
        origin = origin || FALLBACK_ORIGIN;

        let base = Infinity;
        let share = 1;
        let hardLimit = 0;
        let found = false;

        for (const image of this._images) {
            // Before its first tile is dispatched an image has no known origin and draws on the shared
            // budget, so it has to be matched there too or its networkShare would be ignored until then.
            if ((image._schedulerOrigin || FALLBACK_ORIGIN) !== origin) {
                continue;
            }
            found = true;

            // An explicit protocol beats a detected one, and where several images disagree about an origin
            // the most conservative answer wins.
            const declared = this.normalizeProtocol(image.serverProtocol);
            const detected = this._origins.has(origin) ? this._origins.get(origin).protocol : null;
            const protocol = declared || detected;
            base = Math.min(base, protocol ? BASE_LIMITS[protocol] : UNKNOWN_LIMIT);

            // "Use less of this page's bandwidth" is a request that has to be honoured, so the smallest
            // share asked for by any image on the origin applies to all of them.
            const imageShare = typeof image.networkShare === 'number' ? image.networkShare : 1;
            share = Math.min(share, Math.max(0, imageShare));

            // A navigator is a thumbnail of what the main viewer is already loading; it gets a token slot.
            if (this._isNavigatorImage(image)) {
                hardLimit = hardLimit ? Math.min(hardLimit, NAVIGATOR_LIMIT) : NAVIGATOR_LIMIT;
            }

            const jobLimit = image._imageLoader && image._imageLoader.jobLimit;
            if (jobLimit) {
                hardLimit = hardLimit ? Math.min(hardLimit, jobLimit) : jobLimit;
            }
        }

        if (!found) {
            base = UNKNOWN_LIMIT;
        }


        let limit = Math.max(MIN_LIMIT, Math.round(base * share));
        if (hardLimit) {
            limit = Math.min(limit, hardLimit);
        }
        return limit;
    },

    /**
     * @param {String} [origin] falsey means the shared budget
     * @returns {Number} requests currently booked against the origin
     */
    inFlightFor: function(origin) {
        return this._recordFor(origin).inFlight;
    },

    /**
     * Dispatch as many wanted tiles as the origins have room for, taking one tile from each image in turn so
     * that a busy image cannot starve a quiet one. Visibility scores are computed against each image's own
     * pyramid, so they are not comparable across images and cannot be used to order them.
     */
    pump: function() {
        const images = this._images;
        if (!images.length) {
            return;
        }

        let dispatched;
        // Each sweep hands out at most one tile per image. Repeat while anything is still moving, so a
        // completion that frees several slots refills all of them without waiting for the next frame.
        do {
            dispatched = false;
            for (let i = 0; i < images.length; i++) {
                const image = images[(this._cursor + i) % images.length];
                if (this._dispatchOne(image)) {
                    dispatched = true;
                }
            }
            this._cursor = (this._cursor + 1) % images.length;
        } while (dispatched);
    },

    // private
    _dispatchOne: function(image) {
        const wishList = image._wishList;
        if (!wishList || !wishList.length) {
            return false;
        }

        // Only set when the deprecated maxTilesPerFrame option was given explicitly, so that an application
        // relying on it keeps the pacing it asked for.
        if (image.frameDispatchCap && image._dispatchedThisFrame >= image.frameDispatchCap) {
            return false;
        }

        // _schedulerLimit is refreshed once per frame, by the same pass that fills the wish list - so a
        // non-empty wish list always comes with a current limit, and limitFor() (which scans every
        // registered image) does not have to run again on each turn of the dispatch loop.
        if (this.inFlightFor(image._schedulerOrigin) >= image._schedulerLimit) {
            return false;
        }

        while (wishList.length) {
            const tile = wishList.shift();
            // The list was built before the most recent completions landed, so a tile in it may already
            // have been satisfied elsewhere.
            if (tile && !tile.loaded && !tile.loading) {
                image._dispatchedThisFrame++;
                image._loadTile(tile, image._wishListTime);
                return true;
            }
        }
        return false;
    },

    // private
    // The single funnel for every per-origin read and write, and therefore the one place that has to know
    // what an absent origin means: an image whose first tile has not been dispatched yet, or a tile with no
    // real origin at all, draws on the shared budget.
    _recordFor: function(origin) {
        origin = origin || FALLBACK_ORIGIN;
        let record = this._origins.get(origin);
        if (!record) {
            record = { inFlight: 0, protocol: null };
            this._origins.set(origin, record);
        }
        return record;
    },

    // private
    _isNavigatorImage: function(image) {
        // A navigator is itself a Viewer, and its own viewer property points at the one it belongs to.
        return !!(image.viewer && image.viewer.viewer);
    },

    // private
    _startObserving: function() {
        if (this._observing || typeof PerformanceObserver === 'undefined') {
            return;
        }
        this._observing = true;

        const _this = this;
        try {
            // eslint-disable-next-line compat/compat
            this._observer = new PerformanceObserver(function(list) {
                for (const entry of list.getEntries()) {
                    // nextHopProtocol is the empty string for a cross-origin response without a
                    // Timing-Allow-Origin header, which is the default for most tile servers. That is why
                    // serverProtocol can also be stated outright.
                    if (entry.nextHopProtocol) {
                        _this.noteProtocol(_this.originOf(entry.name), entry.nextHopProtocol);
                    }
                }
            });
            // buffered picks up responses that arrived before the first scheduled image was registered.
            this._observer.observe({ type: 'resource', buffered: true });
        } catch (e) {
            $.console.warn('Could not observe resource timing, tile loading concurrency will use the ' +
                'conservative default. Set the serverProtocol option to state it outright.', e);
            this._observer = null;
        }
    },

    /**
     * Drop all state. Intended for tests.
     */
    reset: function() {
        this._origins.clear();
        this._images.length = 0;
        this._cursor = 0;
        if (this._observer) {
            this._observer.disconnect();
            this._observer = null;
        }
        this._observing = false;
    }
};

/**
 * The page-wide tile load scheduler.
 * @member {OpenSeadragon.TileLoadScheduler} tileLoadScheduler
 * @memberof OpenSeadragon
 */
$.tileLoadScheduler = new $.TileLoadScheduler();

}( OpenSeadragon ));
